import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  saveCalculatorData,
} from "src/utils/functions";

const POUPANCA_RATE = 0.0717; // 7.18% a.a.
const SELIC_RATE = 0.1475; // 14.75% a.a.

interface SimulationResult {
  poupanca: number;
  selic: number;
  difference: number;
  monthlyData: Array<{
    month: number;
    poupanca: number;
    selic: number;
  }>;
}

const calculatorKey = "simulador-poupanca-selic";

export const sanitizeBrlValue = (event: { target: { value: string } }) => {
  let value = event.target.value.replace(/\D/g, "");
  if (value) {
    value = (Number.parseInt(value) / 100)
      .toFixed(2)
      .replace(".", ",")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }
  return value;
};

const formatCurrency = (value: number): string => {
  if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(2)}k`;
  return value.toFixed(2);
};

const SavingsSelicSimulator: React.FC = () => {
  const [initialValue, setInitialValue] = useState<string>("1.000,00");
  const [monthlyValue, setMonthlyValue] = useState<string>("100,00");
  const [period, setPeriod] = useState<number>(12);
  const [periodUnit, setPeriodUnit] = useState<"months" | "years">("years");
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const parseValue = (value: string): number => {
    return Number.parseFloat(value.replace(/\./g, "").replace(",", "."));
  };

  const calculateSimulation = (): SimulationResult => {
    const months = periodUnit === "years" ? period * 12 : period;
    const ratePoupanca = Math.pow(1 + POUPANCA_RATE, 1 / 12) - 1;
    const rateSelic = Math.pow(1 + SELIC_RATE, 1 / 12) - 1;

    const calculateForRate = (rate: number, start: number, add: number) => {
      let accumulated: number;
      const monthlyInvested: number[] = [start];
      const monthlyResult: number[] = [];

      if (start === 0) {
        accumulated = ((Math.pow(1 + rate, months) - 1) / rate) * add;
      } else {
        const accumulated1 = start * Math.pow(1 + rate, months);
        const accumulated2 = ((Math.pow(1 + rate, months) - 1) / rate) * add;
        accumulated = accumulated1 + accumulated2;
      }

      let singleMonth = start;
      let singleContribution = start;

      for (let i = 0; i < months; i++) {
        const singleMonthlyTax = singleMonth * rate;
        singleMonth += singleMonthlyTax + add;
        singleContribution += add;

        monthlyResult.push(Number(singleMonth.toFixed(2)));
        monthlyInvested.push(Number(singleContribution.toFixed(2)));
      }

      return {
        accumulated: Number(accumulated.toFixed(2)),
        monthlyResult,
        monthlyInvested,
      };
    };

    const poupancaResult = calculateForRate(
      ratePoupanca,
      parseValue(initialValue),
      parseValue(monthlyValue)
    );
    const selicResult = calculateForRate(
      rateSelic,
      parseValue(initialValue),
      parseValue(monthlyValue)
    );

    const monthlyData = poupancaResult.monthlyResult.map((poupanca, index) => ({
      month: index + 1,
      poupanca,
      selic: selicResult.monthlyResult[index],
    }));

    return {
      poupanca: poupancaResult.accumulated,
      selic: selicResult.accumulated,
      difference: Number(
        (selicResult.accumulated - poupancaResult.accumulated).toFixed(2)
      ),
      monthlyData,
    };
  };

  const handleCalculate = () => {
    const simulationResult = calculateSimulation();
    setResult(simulationResult);
    setCurrentPage(1);
  };

  const paginatedData = useMemo(() => {
    if (!result) return [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    return result.monthlyData.slice(startIndex, startIndex + rowsPerPage);
  }, [result, currentPage]);

  const totalPages = result
    ? Math.ceil(result.monthlyData.length / rowsPerPage)
    : 0;

  const getTickInterval = (dataLength: number) => {
    if (dataLength <= 100) return 10;
    return Math.ceil(dataLength / 10);
  };

  function handleSave() {
    saveCalculatorData(calculatorKey, {
      initialValue,
      monthlyValue,
      period,
      periodUnit,
    });
  }

  useEffect(() => {
    const savedData = loadSavedCalculatorData(calculatorKey);
    if (!savedData) return;
    setInitialValue((savedData.initialValue as string) || "1.000,00");
    setMonthlyValue((savedData.monthlyValue as string) || "100,00");
    setPeriod((savedData.period as number) ? Number(savedData.period) : 12);
    setPeriodUnit((savedData.periodUnit as "months" | "years") || "years");
  }, []);

  return (
    <div className="max-w-7xl mx-auto p-4">
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="text-2xl">
            Simulador de rentabilidade Poupança x Selic
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSave();
              checkCalculatorUsageLimitAndRedirect(handleCalculate);
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label htmlFor="initialValue" className="mb-2 block">
                  Valor inicial
                </Label>
                <Input
                  id="initialValue"
                  type="text"
                  value={initialValue}
                  onChange={(e) => setInitialValue(sanitizeBrlValue(e))}
                  className="w-full"
                  prefix="R$"
                />
              </div>
              <div>
                <Label htmlFor="monthlyValue" className="mb-2 block">
                  Valor mensal
                </Label>
                <Input
                  id="monthlyValue"
                  type="text"
                  value={monthlyValue}
                  onChange={(e) => setMonthlyValue(sanitizeBrlValue(e))}
                  className="w-full"
                  prefix="R$"
                />
              </div>
              <div>
                <Label htmlFor="period" className="mb-2 block">
                  Período
                </Label>
                <div className="flex">
                  <Input
                    id="period"
                    type="number"
                    value={period}
                    onChange={(e) => setPeriod(Number(e.target.value))}
                    className="w-2/3 rounded-r-none"
                  />
                  <Select
                    value={periodUnit}
                    onValueChange={(value: "months" | "years") =>
                      setPeriodUnit(value)
                    }
                  >
                    <SelectTrigger className="w-1/3 rounded-l-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="months">mês(es)</SelectItem>
                      <SelectItem value="years">ano(s)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
              <div className="mb-4 sm:mb-0">
                <span className="text-sm mr-4">
                  Poupança: {POUPANCA_RATE * 100}% a.a
                </span>
                <span className="text-sm">
                  Taxa Selic: {SELIC_RATE * 100}% a.a
                </span>
              </div>
              <Button onClick={handleCalculate} className="w-full sm:w-auto">
                Calcular
              </Button>
            </div>
          </form>
          {result && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">
                      Total acumulado na Poupança
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      R${" "}
                      {result.poupanca.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">
                      Total acumulado na Selic
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-red-600">
                      R${" "}
                      {result.selic.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">
                      Diferença de rentabilidade
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-green-600">
                      R${" "}
                      {result.difference.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="mb-8">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={result.monthlyData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      label={{
                        value: "Mês",
                        position: "insideBottomRight",
                        offset: -10,
                      }}
                      interval={getTickInterval(result.monthlyData.length)}
                    />
                    <YAxis
                      label={{
                        value: "Valor (R$)",
                        angle: -90,
                        position: "insideLeft",
                        offset: 10,
                      }}
                      tickFormatter={formatCurrency}
                    />
                    <Tooltip
                      formatter={(value: number) =>
                        `R$ ${value.toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                        })}`
                      }
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="poupanca"
                      stroke="#8884d8"
                      name="Poupança"
                      dot={false}
                      strokeWidth={3}
                    />
                    <Line
                      type="monotone"
                      dataKey="selic"
                      stroke="#82ca9d"
                      name="Selic"
                      dot={false}
                      strokeWidth={3}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mês</TableHead>
                      <TableHead>Rentabilidade Poupança</TableHead>
                      <TableHead>Rentabilidade Selic</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((data) => (
                      <TableRow key={data.month}>
                        <TableCell>{data.month}</TableCell>
                        <TableCell>
                          R${" "}
                          {data.poupanca.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell>
                          R${" "}
                          {data.selic.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <form
                onSubmit={(event) => event.preventDefault()}
                className="mt-4 flex justify-between items-center"
              >
                <Button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <span>
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SavingsSelicSimulator;
